import React from 'react';
import { makeStyles, Typography, Grid, Button, Theme, Hidden } from '@material-ui/core';
import CircularProgress from '@material-ui/core/CircularProgress';
import LocalVideoPreview from './LocalVideoPreview/LocalVideoPreview';

import { Steps } from '../PreJoinScreens';
import ToggleAudioButton from '../../Buttons/EnhancedToggleAudioButton/EnhancedToggleAudioButton';
import ToggleVideoButton from '../../Buttons/EnhancedToggleVideoButton/EnhancedToggleVideoButton';
import { useAppState } from '../../../state';
import useChatContext from '../../../hooks/useChatContext/useChatContext';
import useVideoContext from '../../../hooks/useVideoContext/useVideoContext';

const useStyles = makeStyles((theme: Theme) => ({
  gutterBottom: {
    marginBottom: '1em',
    [theme.breakpoints.down('sm')]: {
      textAlign: 'center',
    },
  },
  marginTop: {
    marginTop: '1em',
  },
  deviceButton: {
    flex: 1,
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    textWrap: 'nowrap',
    minWidth: '100%',
    border: '2px solid #ddd', // Changed from #aaa to #ddd
    borderRadius: '8px', // Added border radius
    padding: '8px 12px', // Added consistent padding
    fontSize: '14px', // Added font size
    fontWeight: 500, // Added font weight
    minHeight: '45px', // Added min height
    '& .MuiButton-startIcon': {
      marginLeft: '8px',
    },
    '&:hover': {
      borderColor: '#bbb', // Added hover effect
    },
  },
  deviceButtonContainer: {
    display: 'flex',
    flexDirection: 'column',
    paddingTop: '1em',
    paddingBottom: '1em',
  },
  localPreviewContainer: {
    paddingRight: '2em',
    [theme.breakpoints.down('sm')]: {
      padding: '0 2.5em',
    },
  },
  joinButtons: {
    display: 'flex',
    justifyContent: 'space-between',
    marginTop: 'auto', // Added to push to bottom
    [theme.breakpoints.down('sm')]: {
      flexDirection: 'column-reverse',
      width: '100%',
      '& button': {
        margin: '0.5em 0',
      },
    },
  },
  joinButton: {
    padding: '12px 24px',
    fontSize: '16px',
    fontWeight: 600,
    borderRadius: '8px',
    textTransform: 'none',
    '&:disabled': {
      backgroundColor: '#f5f5f5',
      color: '#999',
    },
  },
  mobileButtonBar: {
    [theme.breakpoints.down('sm')]: {
      display: 'flex',
      justifyContent: 'space-between',
      margin: '1.5em',
    },
  },
  mobileButton: {
    padding: '6px 12px', // Changed from 0.8em
    margin: '0 auto',
    flex: 1,
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    maxWidth: '100%',
    fontSize: '14px',
    borderRadius: '8px', // Added border radius
    minHeight: '45px', // Added consistent height
    border: '2px solid #ddd', // Changed from #aaa to #ddd
    '& .MuiButton-startIcon': {
      margin: '0px !important',
      padding: '0px !important',
    },
    [theme.breakpoints.down('sm')]: {
      maxWidth: '180px',
    },
  },
  toggleButtonContainer: {
    display: 'flex',
    width: '100%',
    justifyContent: 'space-between',
    gap: '0.75em',
    marginBottom: '1em',
    flexDirection: 'column',
    [theme.breakpoints.down('sm')]: {
      gap: '0.5em',
      flexDirection: 'row',
      marginBottom: '1em',
    },
  },
}));

interface DeviceSelectionScreenProps {
  name: string;
  roomName: string;
  setStep: (step: Steps) => void;
  actionText: string;
  enableActionButton: string;
  av: string | null | undefined;
}

export default function DeviceSelectionScreen({
  av,
  name,
  roomName,
  setStep,
  actionText,
  enableActionButton,
}: DeviceSelectionScreenProps) {
  const classes = useStyles();
  const { getToken, isFetching } = useAppState();
  const { connect: chatConnect } = useChatContext();
  const { connect: videoConnect, isAcquiringLocalTracks, isConnecting } = useVideoContext();
  const disableButtons = isFetching || isAcquiringLocalTracks || isConnecting;

  const handleJoin = () => {
    getToken(name, roomName).then(({ token }) => {
      // console.log(token)
      videoConnect(token);
      process.env.REACT_APP_DISABLE_TWILIO_CONVERSATIONS !== 'true' && chatConnect(token);
    });
  };

  if (isFetching || isConnecting) {
    return (
      <Grid container justifyContent="center" alignItems="center" direction="column" style={{ height: '100%' }}>
        <div>
          <CircularProgress variant="indeterminate" />
        </div>
        <div>
          <Typography variant="body2" style={{ fontWeight: 'bold', fontSize: '16px' }}>
            Joining Meeting
          </Typography>
        </div>
      </Grid>
    );
  }

  return (
    <>
      <Typography variant="h5" className={classes.gutterBottom}>
        Join {roomName}
      </Typography>

      <Grid container justifyContent="center">
        <Grid item md={7} sm={12} xs={12}>
          <div className={classes.localPreviewContainer}>
            <LocalVideoPreview identity={name} />
          </div>
          <div className={classes.mobileButtonBar}>
            <Hidden mdUp>
              {/* <ToggleAudioButton className={classes.mobileButton} disabled={disableButtons} />
              <ToggleVideoButton className={classes.mobileButton} disabled={disableButtons} /> */}
              {(av == 'audio' || av == 'video') && (
                <ToggleAudioButton className={classes.mobileButton} disabled={disableButtons} buttonText={true} />
              )}
              {av == 'video' && (
                <ToggleVideoButton className={classes.mobileButton} disabled={disableButtons} buttonText={true} />
              )}
            </Hidden>
          </div>
        </Grid>
        <Grid item md={5} sm={12} xs={12}>
          <Grid container direction="column" justifyContent="space-between" style={{ height: '100%' }}>
            <Hidden smDown>
              <div className={classes.toggleButtonContainer}>
                {(av == 'audio' || av == 'video') && (
                  <ToggleAudioButton buttonText={true} className={classes.deviceButton} disabled={disableButtons} />
                )}
                {av == 'video' && (
                  <ToggleVideoButton buttonText={true} className={classes.deviceButton} disabled={disableButtons} />
                )}
              </div>
            </Hidden>

            <div className={classes.joinButtons}>
              <Button
                variant="contained"
                color="primary"
                className={classes.joinButton}
                data-cy-join-now
                onClick={handleJoin}
                disabled={enableActionButton == 'N' ? true : disableButtons}
                fullWidth
              >
                {actionText}
              </Button>
            </div>
          </Grid>
        </Grid>
      </Grid>
    </>
  );
}
